import React, {
    useEffect,
    useState
} from "react";
import {
    useQuery
} from "@tanstack/react-query";
import {
    Modal
} from "../../../../components/modal/modal.jsx";
import Button
    from "../../../../components/button/button.jsx";
import GarrisonService
    from "../../../../services/dispatch/garrison-service.js";
import DispatchService
    from "../../../../services/dispatch/dispatch-service.js";
import {
    FaEye
} from "react-icons/fa";
import {
    DASHBOARD_QUERY_KEYS
} from "../utils/dashboard-query-keys.js";
import {
    useDashboardContext
} from "../context/dashboard-context.js";
import projectUtils
    from "../../../../utils/project-utils.js";
import Loader
    from "../../../../components/template/loader.jsx";
import useDashboardModalState
    from "../hooks/use-dashboard-modal-state.js";
import DashboardInfoTable, {
    DashboardInfoTableCell as Cell,
    DashboardInfoTableHead as HeaderCell,
    DashboardInfoTableTitle as SectionTitle,
} from "./dashboard-info-table.jsx";
import StatusCell
    from "../../utils/components/status-cell.jsx";
import {
    navigateHook
} from "../../../../hooks/use-navigate.js";
import AttachShow
    from "../../../../components/attach/attach-show.jsx";

const DESPACHO_WEB_URL = import.meta.env.VITE_API_DESPACHO_WEB;
const API_URL = import.meta.env.VITE_API_URL ?? "";

const hasValue = value => value !== null && value !== undefined && value !== "";
const formatList = value => Array.isArray(value) ? value.filter(hasValue).join(", ") : value;
const DeviceAlert = ({hasMobile, isGpsOff, isSignal}) => {
    if (!hasMobile) return null;
    const message = isGpsOff ? "DISPOSITIVO COM GPS DESLIGADO" : isSignal === false ? "DISPOSITIVO SEM SINAL" : "";
    if (!message) return null;

    return (
        <div className="border border-[#ebccd1] bg-[#f2dede] px-3 py-2 text-center text-sm font-semibold text-[#a94442]">
            {message}
        </div>
    );
};

const Contingents = ({composition = []}) => {
    const rows = composition.flatMap(item => (item.integrants ?? []).map(integrant => ({
        id: integrant.contingent?.id,
        namewar: integrant.contingent?.namewar,
        compositionName: item.name,
        position: integrant.position,
    })));

    if (!rows.length) return null;

    return (
        <div className="flex flex-row flex-wrap">
            {rows.map((row, index) => (
                <div key={`${row.id}-${index}`} className="my-[2px] ml-2 mr-[-1px] w-[150px] border border-[#ddd] text-center">
                    <div className="flex h-[103px] items-start justify-center border border-[#ddd]">
                        {row.id && (
                            <img
                                src={`${API_URL}/user/getImage/${row.id}`}
                                alt=""
                                className="mt-[5px] block max-h-[90px] max-w-[110px] p-0"
                            />
                        )}
                    </div>
                    <div className="border border-[#ddd] text-[12px] text-[#525252]"><strong>{row.namewar}</strong></div>
                    <div className="border border-[#ddd] text-[11px]">
                        {row.compositionName}
                        <div className="text-[11px]">({row.position})</div>
                    </div>
                </div>
            ))}
        </div>
    );
};

const AnimalRows = ({animals = [], colSpan = 5}) => {
    if (!animals.length) return null;

    return (
        <>
            <tr>
                <HeaderCell colSpan={colSpan}>Animais</HeaderCell>
            </tr>
            <tr>
                <Cell colSpan={colSpan}>{animals.map(animal => animal.name).filter(Boolean).join(" | ")}</Cell>
            </tr>
        </>
    );
};

const formatDispatchSource = (dispatch, dispatchUserName) => {
    const user = dispatchUserName ? ` por ${dispatchUserName}` : "";

    if (dispatch?.source === "WEB_GENERATED") return `Despachado pela central${user}`;
    if (dispatch?.source === "SELF_GENERATED") return `${dispatch.activity ? "Registro de atividade (aplicativo)" : "Auto despacho (aplicativo)"}${user}`;
    if (["LEADER_GENERATED", "EXTERNAL"].includes(dispatch?.source)) return `Despachado pelo responsável${user}`;
    return "";
};

const getSupportAwareValue = (dispatch, field) =>
    dispatch?.supportForGarrisonDispatch === undefined
        ? dispatch?.[field]
        : dispatch?.supportForGarrisonDispatch?.[field];

const GarrisonDetails = ({result}) => {
    const garrison = result?.garrison;
    if (!garrison) return null;

    const materialCaution = result?.materialCaution ?? [];

    return (
        <>
            <DeviceAlert hasMobile={garrison.hasMobile} isGpsOff={result.isGpsOff} isSignal={result.isSignal} />
            <DashboardInfoTable className="mb-4">
                <tbody>
                    <SectionTitle>DADOS INICIAIS</SectionTitle>
                    <tr>
                        <HeaderCell className="w-[20%]">Responsável</HeaderCell>
                        <HeaderCell className="w-[15%]">Equipe</HeaderCell>
                        <HeaderCell className="w-[20%]">Função destinada</HeaderCell>
                        <HeaderCell className="w-[20%]">Local pré-cadastrado</HeaderCell>
                        <HeaderCell className="w-[25%]">Setor</HeaderCell>
                    </tr>
                    <tr>
                        <Cell>{garrison.leaderId ? result.leader : ""}</Cell>
                        <Cell>{garrison.team}</Cell>
                        <Cell>{garrison.intendedFunction}</Cell>
                        <Cell>{garrison.place}</Cell>
                        <Cell>{formatList(garrison.sectors)}</Cell>
                    </tr>
                    {garrison.initialObservation && (
                        <>
                            <tr><HeaderCell colSpan={5}>Observação inicial</HeaderCell></tr>
                            <tr><Cell className="break-words" colSpan={5}>{garrison.initialObservation}</Cell></tr>
                        </>
                    )}
                    <tr><HeaderCell colSpan={5}>Guarnição</HeaderCell></tr>
                    <tr>
                        <Cell colSpan={5}>
                            <div className="mb-[7px]">{garrison.name}</div>
                            <Contingents composition={garrison.composition ?? []} />
                        </Cell>
                    </tr>
                    <AnimalRows animals={garrison.animalModel ?? []} />
                </tbody>
            </DashboardInfoTable>

            {materialCaution.length > 0 &&
                <DashboardInfoTable className="mb-4">
                    <tbody>
                        <SectionTitle colSpan={6}>MATERIAIS EM CAUTELA</SectionTitle>
                        <tr>
                            <HeaderCell className="w-[20%]">Material</HeaderCell>
                            <HeaderCell className="w-[15%]">Nº de série</HeaderCell>
                            <HeaderCell className="w-[15%]">Nº de patrimônio</HeaderCell>
                            <HeaderCell className="w-[15%]">Retirada</HeaderCell>
                            <HeaderCell className="w-[15%]">Devolução/Baixa</HeaderCell>
                            <HeaderCell className="w-[20%]">Integrante</HeaderCell>
                        </tr>
                        {materialCaution.map((material, index) => (
                            <tr key={`${material.id ?? "material"}-${index}`}>
                                <Cell>{material.material}</Cell>
                                <Cell>{material.serial ?? ""}</Cell>
                                <Cell>{material.asset ?? ""}</Cell>
                                <Cell>{material.cautionDate ?? ""}</Cell>
                                <Cell>{material.returnDate ?? ""}</Cell>
                                <Cell>{material.responsibleName}</Cell>
                            </tr>
                        ))}
                    </tbody>
                </DashboardInfoTable>
            }
        </>
    );
};

const DispatchDetails = ({result, boName}) => {
    const [selectedAttachment, setSelectedAttachment] = useState(null);
    const navigate = navigateHook();
    const dispatch = result?.dispatch;
    if (!dispatch) return null;

    const sectors = dispatch.supportForGarrisonDispatch === undefined
        ? dispatch.sectors
        : dispatch.supportForGarrisonDispatch?.sectors;

    const openBo = () => {
        const occurrenceNumber = String(dispatch.occurrenceNumber).replace("/", "-")
        if (!hasValue(occurrenceNumber)) return "";

        const url = `/bo/redirect/${occurrenceNumber}`
        navigate(url, true);
    }

    const openDispatch= () => {
        const url = `${DESPACHO_WEB_URL}/dispatch/form/${dispatch.id}`
        navigate(url, true);
    }

    return (
        <>
            <DeviceAlert hasMobile={dispatch.garrison?.hasMobile} isGpsOff={result.isGpsOff} isSignal={result.isSignal} />
            <DashboardInfoTable>
                <tbody>
                    <SectionTitle>DADOS INICIAIS</SectionTitle>
                    <tr>
                        <HeaderCell>Natureza</HeaderCell>
                        <HeaderCell>Talão</HeaderCell>
                        <HeaderCell>{boName}</HeaderCell>
                        <HeaderCell>Localizador do {boName}</HeaderCell>
                        <HeaderCell>Origem</HeaderCell>
                    </tr>
                    <tr>
                        <Cell>{dispatch.nature}</Cell>
                        <Cell>
                            <p className="cursor-pointer text-[#428bca] hover:underline" onClick={openDispatch}>
                                {dispatch.numberCheckbook ?? "Sem talão"}
                            </p>
                        </Cell>
                        <Cell>
                            {dispatch.occurrenceNumber ? (
                                <p className="cursor-pointer text-[#428bca] hover:underline" onClick={openBo}>
                                    {dispatch.occurrenceNumber}
                                </p>
                            ) : ""}
                        </Cell>
                        <Cell>{dispatch.occurrenceNumberCode}</Cell>
                        <Cell>{formatDispatchSource(dispatch, result.dispatchUserName)}</Cell>
                    </tr>
                    {dispatch.supervisionGarrison && (
                        <>
                            <tr><HeaderCell colSpan={5}>Fiscalizando a guarnição</HeaderCell></tr>
                            <tr><Cell colSpan={5}>{dispatch.supervisionGarrison.name}</Cell></tr>
                        </>
                    )}
                    {dispatch.initialObservation && (
                        <>
                            <tr><HeaderCell colSpan={5}>Observação inicial</HeaderCell></tr>
                            <tr><Cell className="break-words" colSpan={5}>{dispatch.initialObservation}</Cell></tr>
                        </>
                    )}
                    <tr><HeaderCell colSpan={5}>Guarnição</HeaderCell></tr>
                    <tr>
                        <Cell colSpan={5}>
                            <div className="mb-2">{dispatch.garrison?.name}</div>
                            <Contingents composition={dispatch.garrison?.composition ?? []} />
                        </Cell>
                    </tr>
                    <AnimalRows animals={dispatch.garrison?.animalModel ?? []} />
                </tbody>
            </DashboardInfoTable>

            <DashboardInfoTable>
                <tbody>
                    <SectionTitle colSpan={4}>ENDEREÇO DO FATO</SectionTitle>
                    <tr>
                        <HeaderCell>Tipo</HeaderCell>
                        <HeaderCell>Local pré-cadastrado</HeaderCell>
                        <HeaderCell>Logradouro</HeaderCell>
                        <HeaderCell>Número</HeaderCell>
                    </tr>
                    <tr>
                        <Cell>{getSupportAwareValue(dispatch, "factType")}</Cell>
                        <Cell>{getSupportAwareValue(dispatch, "factPlace")}</Cell>
                        <Cell>{getSupportAwareValue(dispatch, "factStreet")}</Cell>
                        <Cell>{getSupportAwareValue(dispatch, "factNumber")}</Cell>
                    </tr>
                    <tr>
                        <HeaderCell>Bairro</HeaderCell>
                        <HeaderCell>Cruzamento</HeaderCell>
                        <HeaderCell>Ponto de referência</HeaderCell>
                        <HeaderCell>Município</HeaderCell>
                    </tr>
                    <tr>
                        <Cell>{getSupportAwareValue(dispatch, "factNeighborhood")}</Cell>
                        <Cell>{getSupportAwareValue(dispatch, "factCrossroads")}</Cell>
                        <Cell>{getSupportAwareValue(dispatch, "factReferencePoint")}</Cell>
                        <Cell>{getSupportAwareValue(dispatch, "factCity")}</Cell>
                    </tr>
                    {sectors && (
                        <tr>
                            <HeaderCell>Setores</HeaderCell>
                            <Cell colSpan={3}>{formatList(sectors)}</Cell>
                        </tr>
                    )}
                </tbody>
            </DashboardInfoTable>

            <DashboardInfoTable className="mb-4">
                <tbody>
                    <SectionTitle>DESLOCAMENTOS</SectionTitle>
                    <tr>
                        <HeaderCell>Destino</HeaderCell>
                        <HeaderCell>Horário (partida)</HeaderCell>
                        <HeaderCell>Km (partida)</HeaderCell>
                        <HeaderCell>Horário (chegada)</HeaderCell>
                        <HeaderCell>Km (chegada)</HeaderCell>
                    </tr>
                    {(dispatch.displacement ?? []).map((displacement, index) => (
                        <tr key={`${displacement.id ?? "displacement"}-${index}`}>
                            <Cell>{displacement.destiny ?? [displacement.destinyStreet, displacement.destinyNumber].filter(Boolean).join(", ")}</Cell>
                            <Cell>{projectUtils.formatDate(displacement.goingDate)}</Cell>
                            <Cell>{displacement.goingMileage}</Cell>
                            <Cell>{projectUtils.formatDate(displacement.arrivalDate)}</Cell>
                            <Cell>{displacement.arrivalMileage}</Cell>
                        </tr>
                    ))}
                </tbody>
            </DashboardInfoTable>

            {(result.supports ?? []).length > 0 && (
                <DashboardInfoTable className="mb-4">
                    <tbody>
                        <SectionTitle colSpan={4}>APOIOS</SectionTitle>
                        <tr>
                            <HeaderCell>Status</HeaderCell>
                            <HeaderCell>Guarnição</HeaderCell>
                            <HeaderCell>Contingente</HeaderCell>
                            <HeaderCell>Nº talão</HeaderCell>
                        </tr>
                        {result.supports.map((support, index) => (
                            <tr key={`${support.id ?? "support"}-${index}`}>
                                <Cell>{<StatusCell active={!!support.status}/>}</Cell>
                                <Cell>{support.compositionName}</Cell>
                                <Cell>{support.displayContingent}</Cell>
                                <Cell>{support.numberCheckbook || "Sem talão"}</Cell>
                            </tr>
                        ))}
                    </tbody>
                </DashboardInfoTable>
            )}

            {(dispatch.attachment ?? []).length > 0 && (
                <DashboardInfoTable className="mb-4">
                    <tbody>
                        <SectionTitle colSpan={1}>ANEXOS</SectionTitle>
                        {dispatch.attachment.map(attach => {
                            const attachId = attach.reference ?? attach.referenceId ?? attach.attachId ?? attach.id;

                            return (
                                <tr key={attach.id ?? attachId}>
                                    <Cell>
                                        <div className="flex items-center gap-2">
                                            {attachId && (
                                                <button
                                                    type="button"
                                                    className="cursor-pointer text-[#525252]"
                                                    title="Visualizar anexo"
                                                    onClick={() => setSelectedAttachment({attachId, name: attach.name})}
                                                >
                                                    <FaEye />
                                                </button>
                                            )}
                                            {attach.name}
                                        </div>
                                    </Cell>
                                </tr>
                            );
                        })}
                    </tbody>
                </DashboardInfoTable>
            )}

            <AttachShow
                open={!!selectedAttachment}
                setOpen={open => !open && setSelectedAttachment(null)}
                attachId={selectedAttachment?.attachId}
                showRemove={false}
                modalTitle={selectedAttachment?.name ?? "Anexo"}
            />
        </>
    );
};

const GarrisonInformationModal = ({payload, boName, onClose}) => {
    const {permissions, navigate} = useDashboardContext();
    const requested = Boolean(payload);
    const garrison = payload?.garrison;
    const garrisonId = garrison?.garrisonId;
    const hasDispatch = garrison?.dispatchStatus === true || hasValue(garrison?.dispatchId);

    const detailsQuery = useQuery({
        queryKey: hasDispatch ? DASHBOARD_QUERY_KEYS.garrisonDispatchDetails(garrisonId) : DASHBOARD_QUERY_KEYS.garrisonDetails(garrisonId),
        queryFn: () => hasDispatch ? DispatchService.getDispatchByGarrisonIdToView(garrisonId) : GarrisonService.getGarrisonByIdToView(garrisonId),
        enabled: requested && hasValue(garrisonId),
    });

    const result = detailsQuery.data;
    const hasDetails = hasDispatch ? Boolean(result?.dispatch) : Boolean(result?.garrison);
    const isReady = !detailsQuery.isFetching && (detailsQuery.isError || (detailsQuery.isSuccess && hasDetails));
    const {open, closeModal, isPreparing} = useDashboardModalState({payload, isReady, onClose});

    useEffect(() => {
        if (detailsQuery.isSuccess && !hasDetails) closeModal();
    }, [closeModal, detailsQuery.isSuccess, hasDetails]);

    const title = hasDispatch ? "Informações do despacho" : "Informações da guarnição";
    const editUrl = hasDispatch && result?.dispatch?.id ? `${DESPACHO_WEB_URL}/dispatch/form/${result.dispatch.id}?redir=despacho/dashboard` : `${DESPACHO_WEB_URL}/garrison/form/${garrisonId}?redir=despacho/dashboard`;
    const canEdit = permissions.garrison;

    const edit = () => {
        navigate(editUrl, true);
    }

    return (
        <>
            {isPreparing && <Loader/>}
            <Modal open={open} setOpen={closeModal} title={title} className="w-full max-w-7xl" isCloseClick={!detailsQuery.isFetching} animated={false}>
            <Modal.Body className="max-h-[calc(100vh-180px)] flex flex-col gap-3 overflow-auto">
                {detailsQuery.isError && (
                    <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                        Não foi possível carregar as informações da guarnição.
                    </div>
                )}

                {!detailsQuery.isError && (
                    hasDispatch ? <DispatchDetails result={result} boName={boName} /> : <GarrisonDetails result={result} />
                )}
            </Modal.Body>
            <Modal.Footer>
                {canEdit && <Button type="button" onClick={edit}>Editar</Button>}
                <Button type="button" target="primary" onClick={closeModal}>Ok</Button>
            </Modal.Footer>
            </Modal>
        </>
    );
};

export default GarrisonInformationModal;
